import { modalHandle } from 'utils/dialog'
import { isMobileDevice } from 'utils/device-detection'

/**
 * Populates dialog image element with QR code data from the opener element's data attributes.
 *
 * @param dialogImage - The image element inside the dialog to populate
 * @param opener - The element that triggered the dialog
 *
 * @example
 * Opener element should have these data attributes:
 * <button data-qr-opener data-qr-src="image.png" data-qr-alt="QR Code" data-qr-width="200" data-qr-height="200">Open QR</button>
 */
const setDialogImageFromOpener = (dialogImage: HTMLImageElement, opener: HTMLElement): void => {
  const qrSrc = opener.getAttribute('data-qr-src') ?? ''
  const qrAlt = opener.getAttribute('data-qr-alt') ?? ''
  const qrWidth = opener.getAttribute('data-qr-width')
  const qrHeight = opener.getAttribute('data-qr-height')

  if (!qrSrc) {
    return
  }

  dialogImage.src = qrSrc
  dialogImage.alt = qrAlt

  if (qrWidth) {
    dialogImage.width = Number(qrWidth)
  }

  if (qrHeight) {
    dialogImage.height = Number(qrHeight)
  }
}

/**
 * Handles a click on a QR opener element.
 * On desktop: shows the QR modal populated with the opener's QR image.
 * On mobile/touch: redirects to the app download link from `data-app-download-link`.
 *
 * @param opener - The clicked element with `data-qr-*` and `data-app-download-link` attributes
 * @param dialog - The global QR dialog element
 * @param dialogImage - The image element inside the dialog
 */
const handleOpenerClick = (opener: HTMLElement, dialog: HTMLDialogElement, dialogImage: HTMLImageElement): void => {
  if (isMobileDevice()) {
    const link = opener.getAttribute('data-app-download-link')
    if (link) {
      window.location.href = link
    }
    return
  }

  setDialogImageFromOpener(dialogImage, opener)

  if (typeof HTMLDialogElement === 'function') {
    dialog.showModal()
  } else {
    dialog.removeAttribute('hidden')
  }
}

/**
 * Initializes the global QR code popup.
 * Finds all `[data-qr-opener]` elements and attaches click handlers.
 * Requires `[data-qr-dialog-global]` and `[data-qr-dialog-image]` to be present in the DOM.
 */
const initQrPopup = (): void => {
  const dialog = document.querySelector<HTMLDialogElement>('[data-qr-dialog-global]')
  const openers = Array.from(document.querySelectorAll<HTMLElement>('[data-qr-opener]'))

  if (!dialog || !openers.length) {
    console.error('QR popup initialization failed:', {
      dialogFound: Boolean(dialog),
      openerCount: openers.length,
    })
    return
  }

  const dialogImage = dialog.querySelector<HTMLImageElement>('[data-qr-dialog-image]')

  if (!dialogImage) {
    console.error('QR popup initialization failed: dialog image not found.')
    return
  }

  openers.forEach((opener) => {
    opener.addEventListener('click', () => handleOpenerClick(opener, dialog, dialogImage))
  })

  modalHandle(dialog)
}

export default initQrPopup
